import { Link } from "react-router-dom";
import { ChatInput } from "@/components/chat/ChatInput";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ItineraryView } from "@/components/itinerary/ItineraryView";
import type { Itinerary } from "@/lib/api/schemas";

const demoItinerary: Itinerary = {
  days: [
    {
      dayNumber: 1,
      date: "2026-06-14",
      title: "Arrival + Historic Core",
      summary: "Easy first day with central landmarks and local food.",
      items: [
        {
          id: "d1-2",
          time: "12:30",
          type: "hotel",
          title: "Check in: Palazzo Navona",
          description: "Boutique stay near Piazza Navona",
          placeName: "Palazzo Navona",
        },
        {
          id: "d1-3",
          time: "15:00",
          type: "activity",
          title: "Pantheon + Piazza walk",
          description: "Guided route through historic center",
          duration: "2h",
          placeName: "Pantheon",
        },
      ],
    },
  ],
};

const demoMessages = [
  {
    id: "m1",
    role: "assistant" as const,
    content: "Where to today? I can plan flights, hotels, and experiences in one flow.",
  },
];

const discoverCards = [
  {
    title: "Scaligero Castle",
    tag: "Attraction",
    image: "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=900&q=80",
  },
  {
    title: "Aruana Churrascaria",
    tag: "Steakhouse",
    image: "https://images.unsplash.com/photo-1559339352-11d035aa65de?w=900&q=80",
  },
  {
    title: "Live Music in Centro",
    tag: "Pop",
    image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=900&q=80",
  },
  {
    title: "Take our travel quiz",
    tag: "Get started",
    image: "https://images.unsplash.com/photo-1519046904884-53103b34b206?w=900&q=80",
  },
  {
    title: "Create a trip",
    tag: "Tools",
    image: "https://images.unsplash.com/photo-1530521954074-e64f6810b32d?w=900&q=80",
  },
  {
    title: "Creator tools",
    tag: "Explore",
    image: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=900&q=80",
  },
];

const sidebarItems = [
  "Chats",
  "Trips",
  "Explore",
  "Saved",
  "Updates",
  "Inspiration",
  "Create",
];

export default function PlannerPreviewPage() {
  return (
    <div className="h-screen px-3 py-3 md:px-4 md:py-4 lg:px-5">
      <div className="mx-auto h-full max-w-[92rem] overflow-hidden rounded-2xl border border-white/10 bg-[#05070e] text-ink">
        <div className="grid h-full md:grid-cols-[220px_1fr_640px]">
          <aside className="hidden border-r border-white/10 bg-black/35 p-4 md:flex md:flex-col">
            <Link to="/" className="text-2xl font-black tracking-tight text-ink">
              roam<span className="text-warm">.</span>
            </Link>
            <div className="mt-8 space-y-1">
              {sidebarItems.map((item) => (
                <button
                  key={item}
                  className="block w-full rounded-lg px-3 py-2 text-left text-sm text-ink-light transition hover:bg-white/10 hover:text-ink"
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="mt-auto rounded-xl border border-white/15 bg-white/5 p-3 text-xs text-ink-light">
              Logged-in planner preview
            </div>
          </aside>

          <section className="relative min-h-0 border-r border-white/10">
            <div className="px-5 py-4">
              <div className="mb-4 flex items-center gap-2 text-xs text-ink-light">
                <span className="rounded-full border border-white/15 px-2 py-1">Where</span>
                <span className="rounded-full border border-white/15 px-2 py-1">When</span>
                <span className="rounded-full border border-white/15 px-2 py-1">1 traveler</span>
                <span className="rounded-full border border-white/15 px-2 py-1">Budget</span>
              </div>
            </div>

            <div className="h-[calc(100%-120px)] overflow-y-auto px-5 pb-28">
              <div className="mx-auto mt-16 max-w-md text-center">
                <h2 className="text-5xl font-black">Where to today?</h2>
                <p className="mt-4 text-lg text-ink-light">
                  Ask anything travel related and I will build your plan.
                </p>
              </div>

              <div className="mx-auto mt-12 max-w-xl">
                {demoMessages.map((msg) => (
                  <ChatMessage key={msg.id} role={msg.role} content={msg.content} />
                ))}
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0">
              <ChatInput
                onSend={() => undefined}
                disabled
                placeholder="Ask anything (always visible in planner)"
              />
            </div>
          </section>

          <aside className="hidden min-h-0 overflow-y-auto bg-white/[0.04] p-5 md:block">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-3xl font-bold">For you</h3>
              <span className="rounded-full border border-white/15 px-3 py-1 text-xs text-ink-light">
                Map
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {discoverCards.slice(0, 3).map((card) => (
                <article key={card.title} className="group relative overflow-hidden rounded-2xl">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="h-44 w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-sm font-semibold">{card.title}</p>
                    <p className="text-xs text-white/75">{card.tag}</p>
                  </div>
                </article>
              ))}
            </div>

            <h4 className="mt-7 text-2xl font-bold">Get started</h4>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {discoverCards.slice(3).map((card) => (
                <article key={card.title} className="group relative overflow-hidden rounded-2xl">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="h-44 w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-sm font-semibold">{card.title}</p>
                    <p className="text-xs text-white/75">{card.tag}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-7 rounded-2xl border border-white/15 bg-white/5 p-3">
              <h4 className="mb-3 text-lg font-semibold">Today's plan snapshot</h4>
              <ItineraryView itinerary={demoItinerary} />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
